import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router';

import { Button, Notification } from '@xsolla/ui-core';
import { AlertExclamation } from '@xsolla/ui-icons';
import { useModal } from '@xsolla/ui-modal';

import { ChecklistItems } from '../../../api/configurations/checklist/types';
import {
  WidgetDefaultPreviewImage,
  WidgetDefaultSigninImage,
  WidgetDefaultSignupImage,
  WidgetPasswordlessCodeImage,
  WidgetPasswordlessImage,
  WidgetPasswordlessPreviewImage,
  WidgetSocialsImage,
  WidgetSocialsPreviewImage
} from '../../../assets/widget-preview-image';
import {
  CLASSIC_LOGIN,
  PASSWORDLESS_LOGIN,
  PROJECT_LINKS,
  SHADOW_PROJECT_TYPE,
  SOCIAL_LOGIN
} from '../../../constants';
import { useChecklist, useCurrentProject } from '../../../contexts/settings';
import { useNotifications } from '../../../hooks/useNotifications';
import { usePaths } from '../../../routes/routesHelper';
import { RouteParams } from '../../../routes/types';
import { hrefTo } from '../../../utils';
import {
  ClassicLoginSettings,
  ExampleWidgetCard,
  PasswordlessLoginSettings,
  TestWidgetModal,
  TryModal
} from '../../index';
import { ToggleBox } from '../../ui';
import ShadowLoginSettings from './ShadowLoginSettings';
import SocialLoginSettings from './SocialLoginSettings';
import { BlockWrapper, CardsWrapper, ContentWrapper } from './style';

enum LoginAuthMethods {
  classic = 'classic',
  passwordless = 'passwordless',
  social = 'social',
  none = ''
}

const AuthenticationMethodSettings = ({
  history,
  match
}: RouteComponentProps): JSX.Element | null => {
  const { socialConnectionPath } = usePaths();
  const { projectId } = match.params as RouteParams;
  const intl = useIntl();
  const { currentProject, updateCurrentProject, currentProjectType } = useCurrentProject();
  const { enableChecklistItem } = useChecklist(projectId);
  const { showErrorNotify } = useNotifications();

  const [activeMethod, setActiveMethod] = useState<LoginAuthMethods>(LoginAuthMethods.none);
  const [fetching, setFetching] = useState<boolean>(false);

  const [onOpenTryModal, onCloseTryModal] = useModal(
    () => (
      <TryModal
        onClose={onCloseTryModal}
        onSuccess={() => {
          onOpenTestWidgetModal();
          onCloseTryModal();
        }}
      />
    ),
    []
  );
  const [onOpenTestWidgetModal, onCloseTestWidgetModal] = useModal(
    () => <TestWidgetModal projectId={projectId} onClose={onCloseTestWidgetModal} />,
    []
  );

  const { DOCUMENTATION } = PROJECT_LINKS;

  useEffect(() => {
    if (currentProject?.is_email_auth_allowed) {
      setActiveMethod(LoginAuthMethods.passwordless);
    }
    if (currentProject?.authentication_allowed) {
      setActiveMethod(LoginAuthMethods.classic);
    }
  }, [currentProject]);

  const redirectToSettings = () => {
    if (activeMethod === LoginAuthMethods.social) {
      history.push(socialConnectionPath(projectId));
    }
  };

  const SubmitHandle = useCallback(async () => {
    if (!currentProject) {
      return;
    }
    setFetching(true);
    try {
      await updateCurrentProject({
        ...currentProject,
        is_email_auth_allowed: activeMethod === LoginAuthMethods.passwordless,
        is_phone_auth_allowed: activeMethod === LoginAuthMethods.passwordless,
        registration_allowed: activeMethod === LoginAuthMethods.classic,
        authentication_allowed: activeMethod === LoginAuthMethods.classic
      });
      await enableChecklistItem(ChecklistItems.SELECTED_LOGIN_METHOD);
      redirectToSettings();
    } catch (error) {
      showErrorNotify(error);
    } finally {
      setFetching(false);
      onOpenTryModal();
    }
  }, [activeMethod]);

  if (currentProject?.type === SHADOW_PROJECT_TYPE) {
    return <ShadowLoginSettings projectId={currentProject?.id || ''} />;
  }

  if (currentProjectType === PASSWORDLESS_LOGIN) {
    return <PasswordlessLoginSettings projectId={currentProject?.id || ''} />;
  }

  if (currentProjectType === CLASSIC_LOGIN) {
    return <ClassicLoginSettings projectId={currentProject?.id || ''} />;
  }

  if (currentProjectType === SOCIAL_LOGIN) {
    return <SocialLoginSettings projectId={currentProject?.id || ''} />;
  }

  return (
    <ToggleBox title={intl.messages['navigation.authentication-method.label']} openByDefault>
      <ContentWrapper>
        <BlockWrapper>
          <Notification icon={AlertExclamation}>
            {intl.messages['navigation.authentication-method.alert']}
          </Notification>
        </BlockWrapper>
        <BlockWrapper>
          <CardsWrapper>
            <ExampleWidgetCard
              isActive={activeMethod === LoginAuthMethods.classic}
              onClick={() => setActiveMethod(LoginAuthMethods.classic)}
              title={intl.messages['navigation.authentication-method.classic-widget.title']}
              subtitle={intl.formatMessage(
                { id: 'navigation.authentication-method.classic-widget.subtitle' },
                hrefTo({ href: DOCUMENTATION })
              )}
              firstModalImgExample={<WidgetDefaultSigninImage />}
              secondModalImgExample={<WidgetDefaultSignupImage />}
              imageSrc={<WidgetDefaultPreviewImage />}
            />
            <ExampleWidgetCard
              isActive={activeMethod === LoginAuthMethods.passwordless}
              onClick={() => setActiveMethod(LoginAuthMethods.passwordless)}
              title={intl.messages['navigation.authentication-method.passwordless-widget.title']}
              subtitle={intl.formatMessage(
                { id: 'navigation.authentication-method.passwordless-widget.subtitle' },
                hrefTo({ href: DOCUMENTATION })
              )}
              firstModalImgExample={<WidgetPasswordlessImage />}
              secondModalImgExample={<WidgetPasswordlessCodeImage />}
              imageSrc={<WidgetPasswordlessPreviewImage />}
            />
            <ExampleWidgetCard
              isActive={activeMethod === LoginAuthMethods.social}
              onClick={() => setActiveMethod(LoginAuthMethods.social)}
              title={intl.messages['navigation.authentication-method.social-widget.title']}
              subtitle={intl.formatMessage(
                { id: 'navigation.authentication-method.social-widget.subtitle' },
                hrefTo({ href: DOCUMENTATION })
              )}
              firstModalImgExample={<WidgetSocialsImage />}
              imageSrc={<WidgetSocialsPreviewImage />}
            />
          </CardsWrapper>
        </BlockWrapper>
        <Button
          data-test-id="btn-configure-login-method"
          onClick={SubmitHandle}
          disabled={activeMethod === LoginAuthMethods.none}
          fetching={fetching}
          look="default"
          palette="brand"
          size="md">
          {intl.messages['navigation.authentication-method.configure.button']}
        </Button>
      </ContentWrapper>
    </ToggleBox>
  );
};

export default withRouter(AuthenticationMethodSettings);
